import { useRoute, useRuntimeConfig } from '#imports'
import { computed, ref, watch } from 'vue'

import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useDataConsent } from '@backmarket/nuxt-module-tracking/useDataConsent'
import { useLegalStore } from '@backmarket/nuxt-module-tracking/useLegalStore'

import { useBurgerMenuState } from '../../navigation/composables/useBurgerMenuState'
import { POPUP_TYPE, TIME_BEFORE_POPUP } from '../LeadCollectionPopup.constants'
import { canShowPopup } from '../utils/popupPersistence'
import { isExcluded, isIncluded } from '../utils/popupRouteUtils'

function isAllowedInCountry(featureFlag: string, country: string) {
  return featureFlag.includes(country)
}

function getActivePopup() {
  const { isOpen } = useBurgerMenuState()
  const route = useRoute()
  const country = useMarketplace().market.countryCode
  const routeName = route.name as string
  const runtimeConfig = useRuntimeConfig()
  const {
    FF_ENABLED_COUNTRIES_FOR_EMAIL_COLLECTOR_POPUP:
      isEmailCollectorEnabledInCountry,
    FF_ENABLED_COUNTRIES_FOR_APP_DOWNLOAD_POPUP: isAppDownloadEnabledInCountry,
    ENABLE_APPSFLYER_WITH_DIDOMI_COUNTRIES,
  } = runtimeConfig.public

  const isAppsFlyerWithDidomiEnabled =
    ENABLE_APPSFLYER_WITH_DIDOMI_COUNTRIES?.includes(country)
  const appDownloadPopupType = isAppsFlyerWithDidomiEnabled
    ? POPUP_TYPE.APP_DOWNLOAD_NEW
    : POPUP_TYPE.APP_DOWNLOAD

  const shouldDisplayAppDownload = computed(() => {
    return (
      isAllowedInCountry(isAppDownloadEnabledInCountry, country) &&
      isIncluded(appDownloadPopupType, routeName) &&
      canShowPopup(appDownloadPopupType)
    )
  })

  const shouldDisplayEmailCollector = computed(() => {
    const popupType = POPUP_TYPE.EMAIL_COLLECTOR

    return (
      isAllowedInCountry(isEmailCollectorEnabledInCountry, country) &&
      isIncluded(popupType, routeName) &&
      canShowPopup(popupType)
    )
  })

  const activePopup = computed(() => {
    const isMenuOpen = isOpen.value

    // [LIF-3272] Block popup on obsolete-computer event page
    // TODO: [LIF-3273] remove after campaign is over (after october, the 20th 2025)
    const isObsoleteComputerPage = route.params.pageName === 'obsolete-computer'

    if (isExcluded(routeName) || isMenuOpen || isObsoleteComputerPage) {
      return null
    }

    // if both popups can be displayed, we prioritize the app download popup
    if (shouldDisplayAppDownload.value) return appDownloadPopupType
    if (shouldDisplayEmailCollector.value) return POPUP_TYPE.EMAIL_COLLECTOR

    return null
  })

  return {
    activePopup,
    routeName,
  }
}

function canAttachFeature() {
  const hasTimePassed = ref(false)
  const timerStarted = ref(false)

  const legalStore = useLegalStore()
  const { isDidomiEnabled, isCookiesModalDisplayed } = useDataConsent()

  const shouldDisplayCookiesModal = computed(() => {
    return isDidomiEnabled
      ? isCookiesModalDisplayed.value
      : legalStore.isModalOpen
  })

  const startTimer = () => {
    // Start timer when modal is not displayed (either initially or after closing)
    if (!timerStarted.value) {
      timerStarted.value = true
      setTimeout(() => {
        hasTimePassed.value = true
      }, TIME_BEFORE_POPUP)
    }
  }

  if (import.meta.client) {
    watch(
      shouldDisplayCookiesModal,
      (isDisplayed) => {
        if (!isDisplayed) startTimer()
      },
      { immediate: true },
    )

    if (isDidomiEnabled) {
      window.Didomi?.once?.('consent.changed', () => {
        startTimer()
      })
    }
  }

  const canAttachLeadCollection = computed(() => {
    const isModalDisplayed = isDidomiEnabled
      ? window.Didomi?.notice?.isVisible?.()
      : shouldDisplayCookiesModal.value

    return hasTimePassed.value && !isModalDisplayed
  })

  return { canAttachLeadCollection }
}

export function useLeadCollectionPopup() {
  const { activePopup, routeName } = getActivePopup()
  const { canAttachLeadCollection } = canAttachFeature()

  return {
    activePopup,
    canAttachLeadCollection,
    routeName,
  }
}
